import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { toast, ToastContainer } from "react-toastify";
import http from "../../../helpers/http";
import * as helpers from "../../../helpers/helpers";
import { TOAST_SETTINGS } from "../../../utils/siteSettings";
import Captcha from "../../common/captcha";
import SubmitButton from "../../common/SubmitButton";
import PrivacyChoicesLabel from "../../common/PrivacyChoicesLabel";
import MetaGenerator from "../../common/meta-generator";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL", "GA", "HI", "ID", "IL", "IN", "IA",
  "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM",
  "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA",
  "WV", "WI", "WY",
];

// The rights a person can use, in the client's words. `value` is what the backend stores (PrivacyRequestType),
// `confirm` is the checkbox the requester ticks to make that request.
const REQUEST_OPTIONS = [
  {
    value: "do_not_sell",
    label: "Right to opt out of sale and sharing",
    confirm: "I ask RhinoRecon not to sell or share my information with any third party.",
  },
  {
    value: "access",
    label: "Right to request a copy",
    confirm: "I ask RhinoRecon for a copy of the information it holds about me.",
  },
  {
    value: "correct",
    label: "Right to correct",
    confirm: "I ask RhinoRecon to correct my information where it is wrong.",
  },
  {
    value: "delete",
    label: "Right to delete",
    confirm: "I ask RhinoRecon to remove my information.",
  },
];

// "Your Privacy Choices": the CCPA opt-out of sale/sharing (the legal alternative title to "Do Not Sell or
// Share My Personal Information", which requires the official icon beside it), plus requests for a copy, a correction
// or a deletion of the data. Interim, email-based: the request is recorded and handled by our team until opted-out
// owners are suppressed automatically.
export default function DoNotSell() {
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaKey, setCaptchaKey] = useState(0); // a token is single-use: remount to get a fresh one
  const [processing, setProcessing] = useState(false);
  const [reference, setReference] = useState(null);
  const {
    register,
    watch,
    setValue,
    formState: { errors },
    handleSubmit,
  } = useForm({ defaultValues: { requester_relationship: "owner", request_type: "" } });
  const isAgent = watch("requester_relationship") === "authorized_agent";
  const requestOption = REQUEST_OPTIONS.find((o) => o.value === watch("request_type"));
  // A correction can't be made without knowing what is wrong, so its notes are required.
  const isCorrection = requestOption?.value === "correct";
  const notesLabel = isCorrection ? "What is wrong, and what should it say?" : "Anything else we should know (optional)";

  const resetCaptcha = () => {
    setCaptchaToken("");
    setCaptchaKey((k) => k + 1);
  };

  const onSubmit = async (data) => {
    if (!captchaToken) {
      toast.error("Please verify the CAPTCHA", TOAST_SETTINGS);
      return;
    }
    setProcessing(true);
    try {
      const res = await http.post(
        "privacy/do-not-sell",
        helpers.doObjToFormData({
          ...data,
          // An agent name typed before switching back to "owner" stays in form state; never send it.
          agent_name: data.requester_relationship === "authorized_agent" ? data.agent_name : "",
          confirm: data.confirm ? 1 : 0,
          captcha_token: captchaToken,
        })
      );
      setReference(res.data.reference);
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Something went wrong. Please try again.", TOAST_SETTINGS);
      resetCaptcha();
    } finally {
      setProcessing(false);
    }
  };

  // Required text must be more than whitespace: "   " cannot identify a property or a person.
  const withTrim = (rules) =>
    rules.required ? { ...rules, validate: (v) => (v || "").trim() !== "" || rules.required } : rules;

  const field = (name, placeholder, rules = {}, props = {}) => (
    <div className="form_blk">
      <input
        id={`dns-${name}`}
        type="text"
        placeholder={placeholder}
        aria-label={placeholder}
        className="input"
        {...props}
        {...register(name, withTrim(rules))}
      />
      <span className="validation-error">{errors[name]?.message}</span>
    </div>
  );

  return (
    <>
      <ToastContainer />
      <main>
        <MetaGenerator
          page_title="Your Privacy Choices"
          meta_desc="Ask RhinoRecon not to sell or share your personal information, or to give you a copy of it, correct it or delete it."
        />
        <section className="contact_pg">
          <div className="contain">
            <div className="sec_heading text-center">
              <h1>
                <PrivacyChoicesLabel iconHeight={24} />
              </h1>
              <p>Opt out of the sale or sharing of your personal information, or ask us for a copy of it, to correct it or to delete it.</p>
            </div>
            <div className="cmn_blk">
              <div className="contact_flex flex">
                <div className="colL">
                  <div className="inner">
                    <p>
                      We hold information about property owners, such as owner names and contact details linked to a
                      property.
                    </p>
                    <p>
                      Choose what you want under Request Options and fill in this form, or email{" "}
                      <a href="mailto:privacy@rhinorecon.com?subject=Privacy%20Request">
                        privacy@rhinorecon.com
                      </a>{" "}
                      with the subject "Privacy Request". We will act on your request within 15
                      business days and confirm by email.
                    </p>
                    <p>
                      Give the address of the property the information relates to, so we can find your records. If
                      you are acting for the owner as an authorized agent, tell us your name as well. We may need to
                      confirm your identity before we send, correct or delete your information.
                    </p>
                  </div>
                </div>
                <div className="colR">
                  <div className="inner">
                    {reference ? (
                      <div role="status">
                        <h3>Request received</h3>
                        <p>
                          Your reference number is <strong>{reference}</strong>. We have emailed you a confirmation and
                          will act on your request within 15 business days.
                        </p>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit(onSubmit)} noValidate>
                        <h3>Submit a request</h3>
                        {field("full_name", "Full Name", { required: "Name is required." }, { autoComplete: "name" })}
                        {field(
                          "email",
                          "Email Address",
                          {
                            required: "Email is required.",
                            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Please enter a valid email" },
                          },
                          { type: "email", autoComplete: "email" }
                        )}
                        {field("phone", "Phone Number (optional)", {}, { autoComplete: "tel" })}
                        {field("property_address", "Property Street Address", {
                          required: "Property address is required.",
                        })}
                        {field("city", "City")}
                        <div className="form_blk">
                          <select id="dns-state" aria-label="State" className="input" {...register("state", { required: "State is required." })}>
                            <option value="">State</option>
                            {US_STATES.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                          <span className="validation-error">{errors.state?.message}</span>
                        </div>
                        {field("zip", "ZIP Code")}
                        {field("mailing_address", "Mailing Address, if different (optional)")}
                        <div className="form_blk">
                          <select id="dns-relationship" aria-label="Who is making this request" className="input" {...register("requester_relationship")}>
                            <option value="owner">I am the property owner</option>
                            <option value="authorized_agent">I am an authorized agent for the owner</option>
                          </select>
                        </div>
                        {isAgent &&
                          field("agent_name", "Your Name (authorized agent)", {
                            required: "Agent name is required.",
                          })}
                        <div className="form_blk">
                          <select
                            id="dns-request-type"
                            aria-label="Request Options"
                            aria-required="true"
                            aria-invalid={errors.request_type ? "true" : "false"}
                            aria-describedby="dns-request-type-error"
                            className="input"
                            {...register("request_type", {
                              required: "Please choose a request option.",
                              // The checkbox states the chosen request, so a new choice needs a new confirmation.
                              onChange: () => setValue("confirm", false),
                            })}
                          >
                            <option value="">Request Options</option>
                            {REQUEST_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                          <span id="dns-request-type-error" className="validation-error">
                            {errors.request_type?.message}
                          </span>
                        </div>
                        <div className="form_blk">
                          <textarea
                            id="dns-notes"
                            aria-label={notesLabel}
                            aria-required={isCorrection ? "true" : "false"}
                            aria-invalid={errors.notes ? "true" : "false"}
                            aria-describedby="dns-notes-error"
                            className="input"
                            placeholder={notesLabel}
                            {...register("notes", withTrim(isCorrection ? { required: "Tell us what to correct." } : {}))}
                          />
                          <span id="dns-notes-error" className="validation-error">
                            {errors.notes?.message}
                          </span>
                        </div>
                        <div style={{ margin: "8px 0 20px" }}>
                          <label htmlFor="dns-confirm" style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
                            <input
                              id="dns-confirm"
                              type="checkbox"
                              style={{ marginTop: 4, flexShrink: 0 }}
                              {...register("confirm", { required: "Please confirm the request." })}
                            />
                            <span>{requestOption ? requestOption.confirm : "I confirm this request."}</span>
                          </label>
                          <span className="validation-error">{errors.confirm?.message}</span>
                        </div>
                        <div className="blk_form" style={{ marginBottom: 12 }}>
                          <Captcha key={captchaKey} onVerify={(token) => setCaptchaToken(token)} />
                        </div>
                        <div className="line_br"></div>
                        <div className="btn_blk">
                          <SubmitButton classes="site_btn" button_text="Submit Request" isFormProcessing={processing} />
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
